import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TableContainer,
  Typography,
  CircularProgress,
  TablePagination,
  IconButton,
  Box,
  Alert,
  Snackbar,
  TextField,
  Autocomplete,
  InputAdornment,
  Button,
  Checkbox,
  Divider,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import ClearIcon from '@mui/icons-material/Clear';
import CashInForm from './CashInForm';
import DialogButton from './../commons/DialogButton';
import EditableRow from './EditableRow';
import PositionSelector from './PositionSelector';
import { formatValue, toDisplayDate } from '../../utils/utils';
import walletApi from '../../api/wallet';
import { getErrorMessage } from '../../utils/errors';

const columns = [
  { id: 'select', label: '', sortable: false, width: 50 },
  { id: 'Description', label: 'Descricao', sortable: true },
  { id: 'Category.Name', label: 'Categoria', sortable: true },
  { id: 'Ticker', label: 'Posicao', sortable: true },
  { id: 'Currency', label: 'Moeda', sortable: true, hideOnMobile: true },
  { id: 'Value', label: 'Valor', sortable: true },
  { id: 'ReceivedDate', label: 'Data', sortable: true, hideOnSm: true },
  { id: 'actions', label: 'Acoes', sortable: false },
];

const CashInList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const highlightRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [categories, setCategories] = useState([]);
  const [currencies] = useState(['USD', 'BRL']);
  const [errorOpen, setErrorOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (highlightId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = setTimeout(() => {
        setSearchParams({});
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightId, data, setSearchParams]);

  const [filters, setFilters] = useState({
    category: null,
    currency: null,
    startDate: '',
    endDate: '',
    description: '',
    position: null,
  });

  const [orderBy, setOrderBy] = useState('ReceivedDate');
  const [order, setOrder] = useState('desc');

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const sortFieldMap = {
    ReceivedDate: 'receiveddate',
    Description: 'description',
    Value: 'value',
    'Category.Name': 'category',
    Ticker: 'ticker',
    Currency: 'currency',
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
      });

      if (filters.category) queryParams.append('category', filters.category.ID);
      if (filters.currency) queryParams.append('currency', filters.currency);
      if (filters.startDate) queryParams.append('start_date', filters.startDate);
      if (filters.endDate) queryParams.append('end_date', filters.endDate);
      if (filters.description.length >= 3) queryParams.append('description', filters.description);
      if (filters.position) queryParams.append('position_id', filters.position.id);

      const sortBy = sortFieldMap[orderBy] || 'receiveddate';
      queryParams.append('sort_by', sortBy);
      queryParams.append('sort_order', order);

      const [cashInResponse, categoryResponse] = await Promise.all([
        walletApi.get(`/cashin?${queryParams.toString()}`),
        walletApi.get(`/category?category_type=cash_in`),
      ]);

      setCategories(categoryResponse.data.Categories || []);

      if (cashInResponse.status === 200) {
        setData(cashInResponse.data.items || []);
        setTotalItems(cashInResponse.data.totalItens || 0);
      } else if (cashInResponse.status === 204) {
        setData([]);
        setTotalItems(0);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Nao foi possivel carregar os dados.'));
      setErrorOpen(true);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, orderBy, order]);

  useEffect(() => {
    if (filters.description.length >= 3 || filters.description === '') {
      fetchData();
    }
  }, [fetchData, filters.description]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    if (field !== 'description') {
      setPage(0);
    }
  };

  const handleClearAllFilters = () => {
    setFilters({
      category: null,
      currency: null,
      startDate: '',
      endDate: '',
      description: '',
      position: null,
    });
    setPage(0);
  };

  const handleDelete = async (id) => {
    try {
      const result = await walletApi.delete(`/cashin/${id}`);
      if (result.status === 200) {
        setData((prevData) => prevData.filter((item) => item.ID !== id));
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Falha ao excluir o lancamento.'));
      setErrorOpen(true);
    }
  };

  const handleUpdate = async (id, description, categoryId, value, receivedDate, positionId, ticker) => {
    try {
      const payload = {
        id,
        description,
        category_id: categoryId,
        value: String(value),
        received_date: receivedDate,
        position_id: positionId || '',
        ticker: ticker || '',
      };
      await walletApi.put(`/cashin`, payload);
      setData((prevData) =>
        prevData.map((item) =>
          item.ID === id
            ? {
                ...item,
                Description: description,
                Category: categories.find((cat) => cat.ID === categoryId),
                Value: value,
                ReceivedDate: receivedDate,
                PositionId: positionId || '',
                Ticker: ticker || '',
              }
            : item
        )
      );
    } catch (err) {
      setError(getErrorMessage(err, 'Falha ao atualizar o lancamento.'));
      setErrorOpen(true);
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseError = () => setErrorOpen(false);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(data.map((item) => item.ID));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const calculateTotal = () => {
    const itemsToSum = selectedIds.length > 0
      ? data.filter((item) => selectedIds.includes(item.ID))
      : data;
    return itemsToSum.reduce((sum, item) => sum + Number(item.Value || 0), 0);
  };

  const getSelectedCount = () => selectedIds.length;

  const hasActiveFilters =
    filters.category || filters.currency || filters.startDate || filters.endDate || filters.description || filters.position;

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Receitas</Typography>
        <DialogButton buttonText="Nova Receita" size="small">
          <CashInForm onCreate={() => fetchData()} />
        </DialogButton>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Buscar..."
          value={filters.description}
          onChange={(e) => handleFilterChange('description', e.target.value)}
          size="small"
          sx={{ width: 160 }}
          InputProps={{
            endAdornment: filters.description && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => handleFilterChange('description', '')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Autocomplete
          options={categories}
          getOptionLabel={(option) => option.Name || ''}
          value={filters.category}
          onChange={(e, newValue) => handleFilterChange('category', newValue)}
          isOptionEqualToValue={(option, value) => option.ID === value?.ID}
          size="small"
          sx={{ width: 140 }}
          renderInput={(params) => <TextField {...params} placeholder="Categoria" />}
        />
        <Autocomplete
          options={currencies}
          getOptionLabel={(option) => option}
          value={filters.currency}
          onChange={(e, newValue) => handleFilterChange('currency', newValue)}
          size="small"
          sx={{ width: 100 }}
          renderInput={(params) => <TextField {...params} placeholder="Moeda" />}
        />
        <Box sx={{ width: 180 }}>
          <PositionSelector
            value={filters.position}
            onChange={(newValue) => handleFilterChange('position', newValue)}
            onError={(msg) => {
              setError(msg);
              setErrorOpen(true);
            }}
            compact
          />
        </Box>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
          <DatePicker
            label="De"
            value={filters.startDate ? dayjs(filters.startDate) : null}
            onChange={(newValue) => handleFilterChange('startDate', newValue ? newValue.format('YYYY-MM-DD') : '')}
            format="DD/MM/YYYY"
            slotProps={{
              textField: { size: 'small', sx: { width: 200 } },
              field: { clearable: true },
            }}
          />
          <DatePicker
            label="Ate"
            value={filters.endDate ? dayjs(filters.endDate) : null}
            onChange={(newValue) => handleFilterChange('endDate', newValue ? newValue.format('YYYY-MM-DD') : '')}
            format="DD/MM/YYYY"
            slotProps={{
              textField: { size: 'small', sx: { width: 200 } },
              field: { clearable: true },
            }}
          />
        </LocalizationProvider>
        {hasActiveFilters && (
          <Button variant="text" size="small" onClick={handleClearAllFilters} sx={{ textTransform: 'none' }}>
            Limpar
          </Button>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="30vh">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer sx={{ mb: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      sx={{
                        fontWeight: 500,
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        width: column.width,
                        display: column.hideOnMobile
                          ? { xs: 'none', md: 'table-cell' }
                          : column.hideOnSm
                          ? { xs: 'none', sm: 'table-cell' }
                          : 'table-cell',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                      sortDirection={orderBy === column.id ? order : false}
                      padding={column.id === 'select' ? 'checkbox' : 'normal'}
                    >
                      {column.id === 'select' ? (
                        <Checkbox
                          indeterminate={selectedIds.length > 0 && selectedIds.length < data.length}
                          checked={data.length > 0 && selectedIds.length === data.length}
                          onChange={handleSelectAll}
                          size="small"
                        />
                      ) : column.sortable ? (
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : 'asc'}
                          onClick={(e) => handleRequestSort(e, column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length > 0 ? (
                  data.map((item) => (
                    <EditableRow
                      key={item.ID}
                      ref={item.ID === highlightId ? highlightRef : null}
                      item={item}
                      categories={categories}
                      onUpdate={handleUpdate}
                      onDelete={handleDelete}
                      onError={(msg) => {
                        setError(msg);
                        setErrorOpen(true);
                      }}
                      selected={selectedIds.includes(item.ID)}
                      onSelect={() => handleSelectOne(item.ID)}
                      highlighted={item.ID === highlightId}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                        Nenhum lancamento encontrado
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {data.length > 0 && (
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell colSpan={3} sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.875rem' }}>
                      {selectedIds.length > 0 ? `${selectedIds.length} selecionados` : `${data.length} itens`}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }} />
                    <TableCell sx={{ fontWeight: 600, color: 'success.main' }}>
                      {formatValue(calculateTotal(), 'BRL')}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>

          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Por pagina:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </TableContainer>
      )}

      <Snackbar
        open={errorOpen}
        autoHideDuration={4000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CashInList;
